/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, from, fromEvent, of, ReplaySubject, throwError, iif } from 'rxjs';
import { mergeMap, filter, map, take, tap, mapTo, timeout, catchError } from 'rxjs/operators';
import { get, isNil } from 'lodash';
import { Commands } from 'bridge-commands/commands';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { DistributedTracingUtilService } from 'distributed-tracing/lib/services/distributedtracing-util.service';
import { OpenTelemetrySpan } from 'distributed-tracing/lib/distributed-tracing';
import { SpanNames } from 'distributed-tracing/lib/constants/distributedtracing_constants';
import {
    STARFLEET_LOGIN_URL_TEMPLATE_AUTHORIZATION_CODE_FLOW,
    STARFLEET_LOGIN_URL_TEMPLATE_IMPLICIT_FLOW
} from '../constants/constants';
import { StarfleetConfig } from '../starfleet-config';
import {
    OAuthLoginData,
    StarfleetTransitionState,
    AuthErrors,
    OAuthUrl,
    SilentLoginInfo
} from '../starfleet.interface';

const GfnOAuthKey = 'oauth';
const GfnOAuthNoLocalstorage = 'no_localstorage_supported';
const GfnOauthUrlData = 'GfnOauthUrlData';

@Injectable({
    providedIn: 'root'
})
export class OAuthService {
    private logger: Logger;
    private isBrowserPlatform = false;
    private httpSubscription: Subscription = null;
    private authWindow: Window = null;
    private isSameTabLoginFeatureEnabled = false;
    private authSaveDataNotifySubject = new ReplaySubject<StarfleetTransitionState>(1);

    constructor(
        config: StarfleetConfig,
        private bridgeService: BridgeService,
        loggingService: LoggingService,
        private distributedTracingUtilService: DistributedTracingUtilService
    ) {
        this.logger = loggingService.getLogger('starfleet/o-auth');
        this.isBrowserPlatform = PlatformDetectorService.isBrowserPlatform();
        this.isSameTabLoginFeatureEnabled = config.sameTabLogin;
    }

    public sameTabAuthStarted(): Observable<StarfleetTransitionState> {
        return this.authSaveDataNotifySubject.asObservable();
    }

    public emitAuthStartNotification(authState: StarfleetTransitionState) {
        this.authSaveDataNotifySubject.next(authState);
    }
    /**
     * Opens OAuth login window and returns an observable that the consumer can
     * subscribe to and get the resulting code or access token
     *
     * @param loginUrl url should include redirect Url
     * @param params such as window height/width and position
     * @returns Observable which resolves to OAuth code (or undefined if error)
     */
    public oAuthLogin(
        config: StarfleetConfig,
        locale: string,
        deviceId: string,
        resolveAtOpen?: Subject<StarfleetTransitionState>,
        idpId?: string,
        preCreatedWindow?: Window,
        returnLocationUrl?: string,
        silentLoginInfo?: SilentLoginInfo,
        redirect?: {
            url: string;
            observable: Subject<string>;
            skipSuccessRedirect?: boolean;
        }
    ): Observable<OAuthLoginData> {
        if (this.isBrowserPlatform) {
            return this.browserClientOAuthLogin(
                config,
                locale,
                deviceId,
                resolveAtOpen,
                idpId,
                preCreatedWindow,
                returnLocationUrl,
                silentLoginInfo,
                redirect
            );
        }

        let redirectSub: Observable<string>;
        let httpSub: Observable<string>;
        if (redirect) {
            redirectSub = of(redirect.url);
            httpSub = redirect.observable;
        } else {
            let portSub;
            [portSub, httpSub] = this.startHttpServer(config, locale);
            redirectSub = portSub.pipe(map(portNum => config.redirectUrl.replace('{portnumber}', portNum.toString())));
        }
        return redirectSub.pipe(
            mergeMap((redirectUrl: string) =>
                this.openOAuthUrl(
                    config,
                    locale,
                    deviceId,
                    resolveAtOpen,
                    redirectUrl,
                    idpId,
                    null,
                    null,
                    silentLoginInfo
                )
            ),
            mergeMap((oAuthUrl: OAuthUrl) =>
                httpSub.pipe(
                    map((requestUrl: string) => this.handleHttpRequest(requestUrl)),
                    map((data: OAuthLoginData) => {
                        data.oAuthUrl = oAuthUrl;
                        return data;
                    })
                )
            ),
            tap((oAuthLoginData: OAuthLoginData) => {
                this.closeHttpSpan(oAuthLoginData, oAuthLoginData.oAuthUrl.span, oAuthLoginData.oAuthUrl.url);
            })
        );
    }

    private browserClientOAuthLogin(
        config: StarfleetConfig,
        locale: string,
        deviceId: string,
        resolveAtOpen: Subject<StarfleetTransitionState>,
        idpId?: string,
        preCreatedWindow?: Window,
        returnLocationUrl?: string,
        silentLoginInfo?: SilentLoginInfo,
        redirect?: {
            url: string;
            observable: Observable<string>;
            skipSuccessRedirect?: boolean;
        }
    ): Observable<OAuthLoginData> {
        const isIframeBasedSilentLogin = silentLoginInfo && silentLoginInfo.silentLoginHelperInstance ? true : false;
        const openAuthUrl$ = this.openOAuthUrl(
            config,
            locale,
            deviceId,
            resolveAtOpen,
            null,
            idpId,
            preCreatedWindow,
            returnLocationUrl,
            silentLoginInfo
        );

        return this.isSameTabLoginFeatureEnabled && !isIframeBasedSilentLogin
            ? openAuthUrl$.pipe(mapTo(null))
            : openAuthUrl$.pipe(
                  mergeMap(oAuthUrl => {
                      this.logger.info('Received response');
                      const evt$ = fromEvent(window, 'message');
                      return iif(() => isIframeBasedSilentLogin, evt$.pipe(timeout(config.maxWaitTimeSSO)), evt$).pipe(
                          map(event => ({ event, oAuthUrl })),
                          catchError(error => {
                              this.logger.info('Error occurred during silent login:', error);
                              return throwError(error);
                          })
                      );
                  }),
                  filter((msg: { event: MessageEvent; oAuthUrl: OAuthUrl }) => this.validateMessageEvent(msg.event)),
                  map((msg: { event: MessageEvent; oAuthUrl: OAuthUrl }) => {
                      const oAuthLoginData = this.getOAuthLoginData(msg.event);

                      if (!oAuthLoginData) {
                          this.logger.error('oAuthLoing Data is null ', oAuthLoginData);
                          throw AuthErrors.OAUTH_DATA_EMPTY;
                      }
                      oAuthLoginData.oAuthUrl = msg.oAuthUrl;

                      this.logger.info('Captured OAuth data via post message: ', oAuthLoginData.error);
                      this.closeHttpSpan(oAuthLoginData, msg.oAuthUrl.span, msg.oAuthUrl.url);
                      if (!isIframeBasedSilentLogin) {
                          if (!redirect?.skipSuccessRedirect) {
                              const redirectUrl = new URL(config.onCompleteRedirectUrl);
                              if (config.redirectParams) {
                                  config.redirectParams.forEach(param => {
                                      if (param in oAuthLoginData) {
                                          redirectUrl.searchParams.append(param, oAuthLoginData[param]);
                                      }
                                  });
                              }

                              msg.oAuthUrl.oAuthWindow.location.href = redirectUrl.href;
                          }
                      } else {
                          silentLoginInfo.silentLoginHelperInstance.closeIframe(oAuthLoginData.oAuthUrl.ifrmEl);
                      }
                      return oAuthLoginData;
                  }),
                  take(1) // ensures that we cancel this observable once it's complete
              );
    }

    private openOAuthUrl(
        config: StarfleetConfig,
        locale: string,
        deviceId: string,
        resolveAtOpen: Subject<StarfleetTransitionState>,
        redirectUrl?: string,
        idpId?: string,
        preCreatedWindow?: Window,
        returnLocationUrl?: string,
        silentLoginInfo?: SilentLoginInfo
    ): Observable<OAuthUrl> {
        const isIframeBasedSilentLogin = silentLoginInfo && silentLoginInfo.silentLoginHelperInstance ? true : false;
        let ifrmEl$ = of(null);

        return this.buildStarfleetUrl(config, locale, deviceId, redirectUrl, idpId, silentLoginInfo).pipe(
            mergeMap(oAuthUrl => {
                let loginUrl;
                // to do span
                const oAuthHttpPopupSpan = this.distributedTracingUtilService.startSpan(SpanNames.StarfleetOAuth);
                if (config.addLogintracer) {
                    loginUrl = this.distributedTracingUtilService.getFormattedUrlForHttpPopupSpan(
                        oAuthUrl.url,
                        oAuthHttpPopupSpan
                    );
                } else {
                    loginUrl = oAuthUrl.url;
                }
                if (preCreatedWindow) {
                    this.authWindow = preCreatedWindow;
                    this.authWindow.location.href = loginUrl;
                } else {
                    if (!isIframeBasedSilentLogin && this.isSameTabLoginFeatureEnabled) {
                        if (!returnLocationUrl) {
                            this.logger.error(
                                'returnLocationUrl Argument is empty ',
                                this.isSameTabLoginFeatureEnabled,
                                returnLocationUrl
                            );
                        }
                        this.logger.info('overwriting login URL');
                        this.emitAuthStartNotification(StarfleetTransitionState.LoginStarted);
                        // Order of subscription and notification in Rxjs Subject is same. This property is used to save data before login and logout.
                        // Deatiled description can be found in MVPC-8165.
                        this.authSaveDataNotifySubject.pipe(take(1)).subscribe(evt => {
                            this.logger.info('Received notification for ', evt);
                            oAuthUrl.returnUrl = returnLocationUrl;
                            window.sessionStorage.setItem(GfnOauthUrlData, JSON.stringify(oAuthUrl));
                            window.open(loginUrl, '_self');
                        });
                    } else if (isIframeBasedSilentLogin) {
                        ifrmEl$ = silentLoginInfo.silentLoginHelperInstance.openIframe(loginUrl);
                    } else if (config.useNativeAuthCommands) {
                        this.bridgeService
                            .executeCommand(Commands.Common.Nativeauth.initiateLogIn, { url: loginUrl })
                            .pipe(take(1))
                            .subscribe();
                    } else {
                        this.authWindow = window.open(loginUrl, '_blank');
                    }
                }
                this.logger.info('login window opened with url', loginUrl);
                if (resolveAtOpen) {
                    resolveAtOpen.next(StarfleetTransitionState.OpenOAuthUrlCompleted);
                }

                oAuthUrl.url = loginUrl;
                oAuthUrl.span = oAuthHttpPopupSpan;
                oAuthUrl.oAuthWindow = this.authWindow;

                if (isIframeBasedSilentLogin) {
                    return ifrmEl$.pipe(
                        map(ifrmEL => {
                            oAuthUrl.ifrmEl = ifrmEL;
                            return oAuthUrl;
                        })
                    );
                }
                return of(oAuthUrl);
            })
        );
    }

    private closeHttpSpan(oAuthLoginData: OAuthLoginData, oAuthHttpPopupSpan: OpenTelemetrySpan, loginUrl: string) {
        if (oAuthLoginData && oAuthLoginData.error) {
            this.distributedTracingUtilService.closeHttpPopupSpan(oAuthHttpPopupSpan, true, {
                errorCode: oAuthLoginData.error,
                errorText: oAuthLoginData.error,
                failedUrl: loginUrl
            });
        } else {
            this.distributedTracingUtilService.closeHttpPopupSpan(oAuthHttpPopupSpan, false, {
                errorCode: '',
                errorText: '',
                failedUrl: loginUrl
            });
        }
    }

    private startHttpServer(
        config: StarfleetConfig,
        locale: string,
        customRedirectParams: string[] = []
    ): [Observable<number>, Observable<string>] {
        const portSub = new Subject<number>();
        const httpMessageSub = new Subject<string>();
        this.httpSubscription = this.bridgeService
            .executeCommand(Commands.Common.Httpserver.StartHttpServer, {
                ports: config.portNumbers,
                redirectUrl: config.onCompleteRedirectUrl,
                redirectParams: this.getRedirectParams(customRedirectParams, config.redirectParams, locale)
            })
            .subscribe(
                response => {
                    this.logger.info('httpresponse recieved');
                    const callBackReason = get(response, 'callbackReason');
                    switch (callBackReason) {
                        case 'serverCreated':
                            portSub.next(get(response, 'portNumber'));
                            portSub.complete();
                            break;

                        case 'httpRequest':
                            httpMessageSub.next(response.url);
                            httpMessageSub.complete();
                            this.stopHttpServer();
                            break;

                        case 'httpRequestAborted':
                            httpMessageSub.error(AuthErrors.LOGIN_CANCELLED_BY_USER);
                            this.stopHttpServer();
                            break;
                    }
                },
                err => {
                    this.logger.error('error starting httpserver', err);
                    portSub.error(AuthErrors.HTTP_START_FAILED);
                    httpMessageSub.error(AuthErrors.HTTP_START_FAILED);
                }
            );
        return [portSub, httpMessageSub];
    }

    private getRedirectParams(
        customRedirectParams: string[],
        configRedirectParams: string[],
        locale: string
    ): string[] {
        const finalConfigRedirectParamsString: string[] = customRedirectParams;
        const customKeys: string[] = [];
        customRedirectParams.forEach(response => customKeys.push(response.split('=')[0]));
        configRedirectParams.forEach(response => {
            if (!(response.split('=')[0] in customKeys)) {
                finalConfigRedirectParamsString.push(response);
            }
        });
        finalConfigRedirectParamsString.push(`ui_locales=${locale}`);
        return finalConfigRedirectParamsString;
    }

    private handleHttpRequest(url: string): OAuthLoginData {
        const authResponse = {} as OAuthLoginData;
        if (url) {
            const urlIn = new URL(url);
            urlIn.searchParams.forEach((value, key) => {
                authResponse[key] = value;
            });
        }

        return authResponse;
    }

    private b64url(array: Uint8Array): string {
        const b64 = btoa(String.fromCharCode.apply(null, array));
        return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }

    /**
     * uses urlParams to generate a Starfleet URL
     *
     * @param urlParams
     */
    private buildStarfleetUrl(
        urlParams: StarfleetConfig,
        locale: string,
        deviceId,
        preConfiguredRedirectUrl?: string,
        idpId?: string,
        silentLoginInfo?: SilentLoginInfo
    ): Observable<OAuthUrl> {
        let url = urlParams.useAuthorizationCodeFlow
            ? STARFLEET_LOGIN_URL_TEMPLATE_AUTHORIZATION_CODE_FLOW
            : STARFLEET_LOGIN_URL_TEMPLATE_IMPLICIT_FLOW;

        url = url.replace(':server', urlParams.url); // replace :server template
        url = url.replace(':clientId', urlParams.clientId); // replace :clientId template
        url = url.replace(':scope', encodeURIComponent(urlParams.scope)); // replace :scope template
        let redirectUrl = '';
        if (preConfiguredRedirectUrl) {
            redirectUrl = preConfiguredRedirectUrl;
        } else {
            redirectUrl = urlParams.redirectUrl;
        }
        url = url.replace(':redirectUri', encodeURIComponent(redirectUrl)); // replace :redirectUri template with encoded Uri
        url = url.replace(':locale', locale); // replace :locale template
        url = url.replace(':deviceId', deviceId);
        const nonce = this.generateNonce();
        url = url.replace(':nonce', nonce);

        if (silentLoginInfo) {
            if (!silentLoginInfo.ssoSharedPageData?.idp_id || !silentLoginInfo.ssoSharedPageData?.login_hint) {
                this.logger.error('Missing login hints for silent login');
                return throwError('Missing login hints for silent login');
            }
            if (silentLoginInfo.silentLoginHelperInstance) {
                url = url.replace('select_account', 'none');
            }
            url += '&idp_id=' + encodeURIComponent(silentLoginInfo.ssoSharedPageData.idp_id);
            url += '&login_hint=' + encodeURIComponent(silentLoginInfo.ssoSharedPageData.login_hint);
        }

        let challenge$: Observable<string> = null;
        let codeVerifier: string = null;
        if (urlParams.useAuthorizationCodeFlow) {
            // Generate a 32-octet random sequence
            const rand = window.crypto.getRandomValues(new Uint8Array(32));
            // The PKCE code verifier is the base64url encoding of the random octets
            codeVerifier = this.b64url(rand);
            // Calculate the SHA-256 hash of the random octets
            const data = new TextEncoder().encode(codeVerifier);
            challenge$ = from(window.crypto.subtle.digest('SHA-256', data)).pipe(
                map(sha256 => {
                    // The PKCE code challenge is the base64url ecnoding of the hash
                    const codeChallenge = this.b64url(new Uint8Array(sha256));
                    url = url.replace(':codeChallenge', codeChallenge);
                    return url;
                })
            );
        } else {
            challenge$ = of(url);
        }

        return challenge$.pipe(
            map(_ => {
                if (!isNil(idpId) && !url.includes('idp_id')) {
                    url = url + '&idp_id=' + idpId;
                }
                return {
                    url,
                    codeVerifier,
                    redirectUrl,
                    nonce
                };
            })
        );
    }

    private generateNonce(): string {
        let d = Date.now();
        const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c: string) {
            /* eslint-disable no-bitwise */
            const r = ((d + Math.random() * 16) % 16) | 0;
            d = Math.floor(d / 16);
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
            /* eslint-enable no-bitwise */
        });
        return uuid;
    }

    public cancelOAuthLogin(
        config: StarfleetConfig,
        locale: string,
        stopHttpServer: boolean
    ): Observable<OAuthLoginData> {
        let portSub: Observable<number> = null;
        let httpSub: Observable<string> = null;
        if (stopHttpServer) {
            this.stopHttpServer();
            httpSub = of('');
        } else {
            [portSub, httpSub] = this.startHttpServer(config, locale, ['error=cancelled']);
        }
        if (this.authWindow) {
            this.authWindow.close();
            this.authWindow = null;
        }
        return httpSub.pipe(map((redirectUrl: string) => this.handleHttpRequest(redirectUrl)));
    }

    private stopHttpServer() {
        if (this.httpSubscription) {
            this.httpSubscription.unsubscribe();
            this.httpSubscription = null;

            this.bridgeService.executeCommand(Commands.Common.Httpserver.StopHttpServer).subscribe(
                success => this.logger.info('success stopping httpserver', success),
                error => this.logger.error('error stopping httpserver', error)
            );
        }
    }

    private validateMessageEvent(event: MessageEvent): boolean {
        if (event.data.key === GfnOAuthNoLocalstorage) {
            this.logger.error('Starfleet Redirect page failure ', event);
            throw AuthErrors.STARFLEET_REDIRECT_FAIL;
        }
        return !!(event && event.data && event.data.key === GfnOAuthKey && event.origin === window.location.origin);
    }

    private getOAuthLoginData(event?: MessageEvent): OAuthLoginData {
        const key = this.isSameTabLoginFeatureEnabled ? GfnOAuthKey : get(event, 'data.key');
        const obj = window.localStorage.getItem(key);
        window.localStorage.removeItem(key);

        this.logger.info('Extracted key for reading oAuthLogin data ', key);
        if (!!obj) {
            try {
                return JSON.parse(obj).data;
            } catch (error) {
                this.logger.error('malformed access code object', obj);
                return null;
            }
        } else {
            return null;
        }
    }

    public readAndValidateoAuthLoginData(): OAuthLoginData {
        const oAuthLoginData = this.getOAuthLoginData();
        if (oAuthLoginData) {
            const obj = window.sessionStorage.getItem(GfnOauthUrlData);
            window.sessionStorage.removeItem(GfnOauthUrlData);
            if (!!obj) {
                try {
                    oAuthLoginData.oAuthUrl = JSON.parse(obj);
                } catch (error) {
                    this.logger.error('malformed access code object', obj);
                }
            }
        }
        this.logger.info('Received oAuth Data on return from login ', oAuthLoginData);
        return oAuthLoginData;
    }
}
